'use strict';

import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import Dropzone from 'react-dropzone';
import { Icon } from '@iconify-icon/react';
import FileXls from '@iconify-icons/ph/file-xls';
import FilePdf from '@iconify-icons/ph/file-pdf';
import FileDoc from '@iconify-icons/ph/file-doc';
import FileImage from '@iconify-icons/material-symbols/image';
import FileOther from '@iconify-icons/ion/document-attach-outline';
import Delete from '@iconify-icons/material-symbols/delete-outline';
import { UPLOAD } from '../../../constants/';
import { Loading } from './Control';
import { Dialog } from './Dialog_v2';

const iconMap = [
	{
		test: /\.(gif|jpe?g|tiff|png|webp|bmp)$/i,
		icon: FileImage,
		openBox: true
	},
	{
		test: /\.docx?$/i,
		icon: FileDoc
	},
	{
		test: /\.xlsx$/i,
		icon: FileXls
	},
	{
		test: /\.pdf$/i,
		icon: FilePdf
	},
	{
		test: /[\s\S]*/,
		icon: FileOther
	}
];

const getFileInfo = (fileName) => {
	for(const item of iconMap) {
		const valid = item.test.test(fileName);
		if (valid) {
			return item || iconMap[iconMap.length - 1];
		}
	}
};

export const ImageBox = ({ imgUrl, imgName, onClose, onDownload, index }) => {
	return <Dialog  {...{
		width: 900,
		height: 600,
		title: `${imgName}`,
		ico: false,
		isClose: false,
		isOk: false,
		cancelText: '關閉',
		close: () => onClose(),
		renderFooter: () => {
			return <>
				<span {... {
					className: 'btn btn-default',
					onClick: () => onDownload(),
				}}>
					下載
				</span>
				<span {... {
					className: 'btn btn-primary',
					onClick: () => onClose(),
				}}>關閉</span>
			</>;
		}
	}}>
		<div style={{
			overflow: 'auto',
			height: '100%'
		}}>
			<img src={imgUrl} alt={imgName} style={{maxWidth: '900px', maxHeight: '600px'}} />
		</div>
	</Dialog>;
};

export const useDownload  = (url, opts, fileName) => {
	const [downloading, setDownloading] = useState(false);
	const downLoadfile = useCallback(() => {
		new _BlobAjax({
			...opts,
			method: 'get',
			url,
			onRequest: () => setDownloading(true),
			onComplete: () => setTimeout(() => setDownloading(false), 1000),
			fileName
		}).send();
	}, [url, opts, fileName]);
	return [downloading, downLoadfile];
};

const DownloadLink = (props) => {
	const { url, fileName, showName, opts = {}, icon = true, imgUrl, onlyView = false } = props;

	const [isOpenBox, setOpenBox] = useState(false);
	const fileInfo = getFileInfo(fileName);
	const [downloading, downLoadfile] = useDownload(url, opts, fileName);

	const onClick = useCallback(() => {
		if (onlyView) return;
		if (fileInfo.openBox && imgUrl) return setOpenBox(true);
		downLoadfile();

	}, [downLoadfile, fileInfo, setOpenBox, imgUrl, onlyView]);

	return <>
		<span className={'-file'}>
			{
				icon && (
					<Icon className='-icon' icon={fileInfo.icon} />
				)
			}
			<span {...{
				className: onlyView ? '' : 'link',
				...!downloading && { onClick },
			}}>
				{showName || fileName || url}
				{downloading && <Loading />}
			</span>
		</span>
		{isOpenBox && imgUrl && <ImageBox imgUrl={imgUrl} imgName={fileName} onClose={() => setOpenBox(false)} onDownload={() => downLoadfile()} />}
	</>;
};

const DownloadFile = React.memo(props => {
	// (png、jpg)、word、excel、pdf
	return <div className={`H2-Attachment${props.className ? ` ${props.className}` : ''}`}>
		<DownloadLink {...props} />
	</div>;
});

class AttachFile extends React.Component {
	static propTypes = {
		accept: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
		maxSize: PropTypes.number,
		multiple: PropTypes.bool,
		onUpdate: PropTypes.func,
		onError: PropTypes.func,
		url: PropTypes.string,
		detachUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
		fileTypeInfo: PropTypes.string,
		fileSizeInfo: PropTypes.string,
		widthBtn: PropTypes.number,
	};
	static defaultProps = {
		multiple: false,
		detachUrl: ({ fileUUID }) => `/api/file/detach/${fileUUID}`,
		fileTypeInfo: OptionUtils.getLabel('components.AttachFile', 'unableToUploadExecutableFileE'),
		fileSizeInfo: OptionUtils.getLabel('components.AttachFile', 'maximumFileSizeMB'),
		removeWhenUnMount: false,	//true: componentWillUnmount()時移除暫存檔
		widthBtn: 0,                //藍框btn width 大小，預設0為自適應寬度
	};
	/*
	* 加上 props.removeWhenUnMount 的原因詳見 http://jira.104.com.tw/browse/EHRMS-3331
	* 簡單說明兩種情境:
	* 1、加附檔的動作在IPEGrid，按取消(x)時，要把暫存檔移除。
	* 2、加附檔的動作在子 Dialog， 關閉後才在父 Dialog 進行確定儲存的動作，故關閉子 Dialog 時暫存檔要留著。
	*/
	state = {
		enable: true,
		attachment: undefined,
		uploading: false,
	};

	removeFromUnMount = false;

	constructor(props) {
		super(props);
		this.upload = this.upload.bind(this);
		this.remove = this.remove.bind(this);
	}

	componentWillUnmount() {
		if (this.props.removeWhenUnMount && this.state.attachment) {
			this.removeFromUnMount = true;
			this.remove();	//移除temp檔
		}
	}

	upload = (acceptedFiles, rejectedFiles, evt) => {
		if (!this.isEnable()) return;

		const { onUpdate, onError, url } = this.props;

		let maxSize = _.get(this.props, 'maxSize', UPLOAD.TMP_FILE_SIZE);
		if (_.size(rejectedFiles) === 1 && rejectedFiles[0].file.size > maxSize) {
			if (onError) onError('max_size_exceed', OptionUtils.getLabel('components.AttachFile', 'filesExceedTheLimitOfMB').replace('3', maxSize / 1024 / 1024));
			return;
		}

		const rejectFileTypes = rejectedFiles.map(file => file.type);

		if (_.some(rejectFileTypes, type => !UPLOAD.TMP_VALID_EXTS.includes(type) || type === '')) {
			// 副檔名有可能無法辨識(e.g. .drawio)，type 會是空字串
			onError('invalid_format', OptionUtils.getLabel('components.AttachFile', 'unsupportedFileTypes'));
			return;
		}

		let min = 1;
		if (acceptedFiles.length < min) {
			if (onError) onError('not_select_min', _.template(OptionUtils.getLabel('components.AttachFile', 'pleaseSelectAtLeastFiles'))({'temp1': min}));
			return;
		}

		let fileName = acceptedFiles[0].name;
		if (_.some(UPLOAD.INVALID_WORDS, c => {return _.includes(fileName, c);})) {
			if (onError) onError('invalid_word', _.template(OptionUtils.getLabel('components.AttachFile', 'theFileNameCannotContainSpecial'))({'temp1': UPLOAD.INVALID_WORDS.join(' ')}));
			return;
		}
		if (fileName.length > 40) {
			if (onError) onError('max_length_exceed', `檔案名稱長度不可超過40個字`);
			return;
		}

		const attach = new Request.File({
			url,
			method: 'post',
			onRequest: () => this.setState({ uploading: true }),
			onSuccess: (code, msg, data) => {
				attach.reset();

				this.setState({
					attachment: data,
					uploading: false,
				});

				if (onUpdate) onUpdate(data);
			},
			onError: (code, msg, data, xhr) => {
				this.setState({
					uploading: false,
				});

				const MSG = {
					450: OptionUtils.getLabel('components.AttachFile', 'fileSizeExceedsLimit'),
				};
				if (onError) onError('upload_fail', _.template(OptionUtils.getLabel('components.AttachFile', 'uploadFailed'))({'temp1': MSG[xhr.status] ? MSG[xhr.status] : ''}));
			},
		});

		acceptedFiles.map(file => {attach.append('attachment', file);});
		attach.send();
	};

	remove = () => {
		if (!this.isEnable()) return;

		const { onUpdate, onError, detachUrl } = this.props;
		const { attachment } = this.state;

		//只能刪除 temp 檔，其他則在 new or update 做處理
		let isTmp = _.get(attachment, 'isTmp', 1);
		if (!isTmp) {
			this.setState({ attachment: undefined });
			if (onUpdate) onUpdate('');
			return;
		}

		new Ajax({
			url: typeOf(detachUrl) === 'string' ? detachUrl + '/' + attachment.fileUUID : detachUrl(attachment),
			method: 'get',
			onRequest: () => {},
			onSuccess: (code, msg, data, xhr) => {
				if (!this.removeFromUnMount) {
					this.removeFromUnMount = false;
					this.setState({ attachment: undefined });
				}

				if (onUpdate) {
					onUpdate('');
				}
			},
			onError: (code, msg, data, xhr) => {
				this.setState({
					uploading: false,
				});

				if (onError) onError('upload_fail', OptionUtils.getLabel('components.AttachFile', 'deleteFailed'));
			},
		}).send();
	};

	reset() {
		if (!this.isEnable()) return this;
		this.setState({ attachment: undefined });
		return this;
	}

	validate() {
		const { onError } = this.props;
		const { attachment } = this.state;

		let required = _.get(this.props, 'required', false);
		if (required && _.isEmpty(attachment)) {
			if (onError) onError('not_select_empty', OptionUtils.getLabel('components.AttachFile', 'noFileSelected'));
			return false;
		}

		return true;
	}

	/**
	 * <b>暫時沒有 true 的情境，先統一回傳 false 跟 React Picker 對齊 interface</b><br/>
	 * 是否為檢視中(無權更改)<br/>
	 * isView() == true，getValue() 仍會取得值<br/>
	 * isEnable() == false，getValue() 會得到 undefined<br/>
	 * @returns {Boolean} true if 檢視中(無權更改); else false
	 */
	isView() { return false; }

	isEnable() { return this.state.enable; }

	enable() {
		this.setState({ enable: true });
		return this;
	}

	disable() {

		this.setState({ enable: false });
		return this;
	}

	isEditable() { return this.state.enable; }

	editable() {
		this.setState({ enable: true });
		return this;
	}

	readOnly() {
		this.setState({ enable: false });
		return this;
	}

	getValue() {
		if (!this.isEnable()) return '';
		return this.state.attachment;
	}

	setValue(v, fireEvent = true) {
		if (!this.isEnable()) return this;
		let fileUUID = _.get(v, 'fileUUID', undefined);
		let fileSize = _.get(v, 'fileSize', 0);
		let fileName = _.get(v, 'fileName', '');
		let isTmp = _.get(v, 'isTmp', 1);
		let attachment = fileUUID ? { fileUUID, fileSize, fileName, isTmp } : undefined;
		this.setState({ attachment });
		return this;
	}

	render() {
		const { multiple, accept, maxSize, fileTypeInfo, fileSizeInfo, imgUrl, disable, widthBtn } = this.props;
		const { attachment, uploading } = this.state;
		const fileUUID = _.get(attachment, 'fileUUID', undefined);
		const fileName = _.get(attachment, 'fileName', '');
		const isDisable = disable || !this.isEnable();

		let downloadUrl;
		if (fileUUID) {
			let isTmp = _.get(attachment, 'isTmp', 1);
			if (isTmp) {
				// downloadUrl = _.get(attachment, 'fileName', '');
				downloadUrl = (
					<div className={'-file'} style={widthBtn > 0 ? { width: widthBtn } : {}}>
						<Icon className='-icon' icon={getFileInfo(fileName).icon} />
						<span>
							{_.get(attachment, 'fileName', '')}
						</span>
					</div>
				);
			} else {
				// downloadUrl = <DownloadFile icon={false} url={H2.utils.downloadUrl(fileUUID)} fileName={_.get(attachment, 'fileName', '')} />;
				downloadUrl = <DownloadLink {...{
					url: H2.utils.downloadUrl(fileUUID),
					fileName,
					imgUrl
				}}/>;
			}
		}

		return fileUUID
			? <div className="H2-Attachment" style={{ 'alignItems': 'flex-start', 'marginBottom': '8px' }}>
				{downloadUrl}
				{/* <div style={{ 'width': '80px', 'height': '25px', 'marginTop': '2px' }}>
					<span className="-size">({(attachment.fileSize * 1 / 1024).toInt().format() + ' Kb'})</span>
				</div> */}
				<div className={'-remove'} onClick={this.remove}>
					<Icon className='H2-Icon -interact' icon={Delete} />
				</div>
			</div>
			: <>
			<div className={`H2-Upload inlineBlock${isDisable ? ' disable' : ''}`}>
				<div className = "form-group inlineBlock" style={{ 'marginLeft': '0px', 'marginRight': '5px' }}>
					<Dropzone {...{
						multiple,
						accept: accept ? accept : UPLOAD.TMP_VALID_EXTS,
						maxSize: maxSize ? maxSize : UPLOAD.TMP_FILE_SIZE,
						disabled: uploading || disable,
						onDrop: this.upload,
					}}>{
						({ getRootProps, getInputProps }) => (
							<div {...getRootProps({ style: { outline: 'none' } })}>
								<input {...getInputProps()} />
								<div className="-btn" style={widthBtn > 0 ? { width: widthBtn } : {}}>
									{uploading ? <i className="loading" style={{ 'marginRight': '3px' }} /> : null}
									<img style={{ marginRight: '6px' }} src="img/upload.svg" alt="icon" />
									<span className="-text">
										{widthBtn == 0 ? OptionUtils.getLabel('components.AttachFile', 'uploadTheFile') : _.isEqual(user.locale, 'en_US') ? 'Upload' : OptionUtils.getLabel('components.AttachFile', 'uploadTheFile') }
									</span>
									{fileTypeInfo||fileSizeInfo ? <span className="-note">
										{fileTypeInfo ?
											<span>{fileTypeInfo}</span> : null}
										{fileSizeInfo ? <span>{fileSizeInfo}</span> : null}
									</span>:null}
								</div>
							</div>
						)
					}</Dropzone>
				</div>
			</div>
		</>;
	}
}

export { AttachFile, DownloadFile };
